/**
 * businessStore — Zustand store for the active business + available list.
 *
 * Day 15 build-fix (Jun 11):
 *   - Implements clearBusinessState() — authStore calls it on logout.
 *   - Typed via BusinessState so availableBusinesses.find(b => ...) infers Business.
 *
 * Day 15 refresh-fix (Jun 13):
 *   - Persists activeBusinessId alongside activeBusinessSlug so the
 *     X-Business-Id header resolves on a cold page load before
 *     availableBusinesses refetches.
 *
 * Block C0 industry-refresh-fix (Jun 20):
 *   - Persists activeBusinessIndustryType (same bug class as the id race) so
 *     useIndustry doesn't flash to the "retail" default on a cold refresh.
 *
 * Block C1c LIVE-refresh-fix (Jun 21):
 *   - NEW action applyIndustryChange(businessId, industryType). The Settings
 *     IndustryPicker change went through useSetIndustry, which only
 *     invalidated React Query caches — but useIndustry reads from THIS store,
 *     not React Query, so nothing on screen updated until a full re-login.
 *     This action writes the new industryType straight into the store: it
 *     patches the business inside availableBusinesses (so useIndustry's
 *     authoritative path re-derives immediately) AND updates the persisted
 *     activeBusinessIndustryType when the changed business is the active one
 *     (so a refresh right after stays correct). Server reconcile still happens
 *     via the query invalidations in useSetIndustry.
 *
 * Persistence: activeBusinessSlug + activeBusinessId + activeBusinessIndustryType
 * persist; availableBusinesses re-fetches from /businesses/mine on login.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Business, BusinessState } from "@/types/business.types";

/** Resolve a business _id from a slug (or id) against a list. */
function resolveId(
  slug: string | null,
  list: Business[] | undefined,
): string | null {
  if (!slug || !Array.isArray(list)) return null;
  const match = list.find((b) => b?.slug === slug || b?._id === slug);
  return match?._id ?? null;
}

/**
 * Resolve a business's industryType from a slug (or id) against a list.
 * Returns null when the list hasn't loaded or the business carries no type
 * — callers keep any previously-persisted value in that case.
 */
function resolveIndustryType(
  slug: string | null,
  list: Business[] | undefined,
): string | null {
  if (!slug || !Array.isArray(list)) return null;
  const match = list.find((b) => b?.slug === slug || b?._id === slug);
  return match?.industryType ?? null;
}

/** Match a business in the list by either _id or id. */
function businessMatchesId(b: Business, businessId: string): boolean {
  return b?._id === businessId || b?.id === businessId;
}

const useBusinessStore = create<BusinessState>()(
  persist(
    (set, get) => ({
      activeBusinessSlug: null,
      activeBusinessId: null,
      activeBusinessIndustryType: null,
      availableBusinesses: [] as Business[],
      activeBranchByBusiness: {} as Record<string, string | null>,
      pageBusinessId: null,
      setPageBusinessId: (id: string | null) => set({ pageBusinessId: id }),

      setActiveBranch: (businessId: string, branchId: string | null) =>
        set({ activeBranchByBusiness: { ...get().activeBranchByBusiness, [businessId]: branchId } }),

      setActiveBusinessSlug: (slug: string | null) => {
        // Group Mode (slug === null) clears id + industry too.
        if (slug === null) {
          set({
            activeBusinessSlug: null,
            activeBusinessId: null,
            activeBusinessIndustryType: null,
            pageBusinessId: null,
          });
          return;
        }
        const list = get().availableBusinesses;
        const resolvedId = resolveId(slug, list);
        const resolvedIndustry = resolveIndustryType(slug, list);
        set({
          pageBusinessId: null,
          activeBusinessSlug: slug,
          // Keep previously-persisted values if the list hasn't loaded yet.
          activeBusinessId: resolvedId ?? get().activeBusinessId,
          activeBusinessIndustryType:
            resolvedIndustry ?? get().activeBusinessIndustryType,
        });
      },

      // Alias of setActiveBusinessSlug — kept for backward compat.
      setActiveBusiness: (slug: string | null) => {
        if (slug === null) {
          set({
            activeBusinessSlug: null,
            activeBusinessId: null,
            activeBusinessIndustryType: null,
            pageBusinessId: null,
          });
          return;
        }
        const list = get().availableBusinesses;
        const resolvedId = resolveId(slug, list);
        const resolvedIndustry = resolveIndustryType(slug, list);
        set({
          pageBusinessId: null,
          activeBusinessSlug: slug,
          activeBusinessId: resolvedId ?? get().activeBusinessId,
          activeBusinessIndustryType:
            resolvedIndustry ?? get().activeBusinessIndustryType,
        });
      },

      setAvailableBusinesses: (businesses: Business[]) => {
        // When the list arrives after a refresh, back-fill both the id and
        // the industryType for the already-persisted slug so the interceptor
        // can resolve the header AND useIndustry reconciles to the real
        // industry (replacing the persisted snapshot with authoritative data).
        const slug = get().activeBusinessSlug;
        const resolvedId = resolveId(slug, businesses);
        const resolvedIndustry = resolveIndustryType(slug, businesses);
        set({
          availableBusinesses: businesses,
          activeBusinessId: resolvedId ?? get().activeBusinessId,
          activeBusinessIndustryType:
            resolvedIndustry ?? get().activeBusinessIndustryType,
        });
      },

      // ── Block C1c: live industry change ────────────────────────────
      // Patch the changed business in-place + (if it's the active one) update
      // the persisted snapshot, so useIndustry re-derives on the next render
      // with no re-login. Idempotent and a no-op if the business isn't found.
      applyIndustryChange: (businessId: string, industryType: string) => {
        const state = get();
        const updatedList = state.availableBusinesses.map((b) =>
          businessMatchesId(b, businessId) ? { ...b, industryType } : b,
        );
        const changed = updatedList.find((b) =>
          businessMatchesId(b, businessId),
        );
        const isActive =
          (!!changed && changed.slug === state.activeBusinessSlug) ||
          state.activeBusinessId === businessId;
        set({
          availableBusinesses: updatedList,
          activeBusinessIndustryType: isActive
            ? industryType
            : state.activeBusinessIndustryType,
        });
      },

      clearBusinessState: () =>
        set({
          activeBusinessSlug: null,
          activeBusinessId: null,
          activeBusinessIndustryType: null,
          availableBusinesses: [],
          activeBranchByBusiness: {},
          pageBusinessId: null,
        }),

      // Alias
      reset: () =>
        set({
          activeBusinessSlug: null,
          activeBusinessId: null,
          activeBusinessIndustryType: null,
          availableBusinesses: [],
          activeBranchByBusiness: {},
          pageBusinessId: null,
        }),
    }),
    {
      name: "eaze-business-store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        activeBusinessSlug: state.activeBusinessSlug,
        activeBusinessId: state.activeBusinessId,
        activeBusinessIndustryType: state.activeBusinessIndustryType,
        activeBranchByBusiness: state.activeBranchByBusiness,
      }),
    },
  ),
);

export default useBusinessStore;
